import { computed, reactive, ref } from "vue";
import { logger } from "@/infrastructure/services/LoggerService";
import { resolveApiErrorKey } from "@/infrastructure/services/ApiErrorService";
import { createDefaultTaxReceiptSettings, daysInMonth, formatTaxReceiptNumberPreview, isValidCampaignFirstFiscalYear, isValidNumberingPrefix, isValidTriggerDate, TAX_RECEIPT_COUNTER_SIZES, TAX_RECEIPT_MAX_NOTIFICATION_RECIPIENTS, TAX_RECEIPT_NUMBERING_MODES, } from "../domain/entities/taxReceiptSettings";
import { getTaxReceiptSettings, updateTaxReceiptSettings, } from "../infrastructure/adapters/taxReceiptSettingsService";
import { fromRecipientOptionIds, toRecipientOptionIds } from "./useTaxReceiptNotificationRecipients";
const ERR = "finance.taxReceipts.settings.errors";
/** Backend codes → i18n keys (keep in sync with OPDON `TaxReceiptSettingsErrorCodes`). */
export const TAX_RECEIPT_SETTINGS_ERROR_KEYS = {
    "TaxReceiptSettings.Mode.Required": `${ERR}.mode`,
    "TaxReceiptSettings.Mode.Invalid": `${ERR}.mode`,
    "TaxReceiptSettings.CampaignTriggerDate.Invalid": `${ERR}.triggerDate`,
    "TaxReceiptSettings.CampaignFirstFiscalYear.Invalid": `${ERR}.campaignFirstFiscalYearRecent`,
    "TaxReceiptSettings.CampaignNotificationRecipients.Invalid": `${ERR}.recipients`,
    "TaxReceiptSettings.NumberingMode.Required": `${ERR}.numberingMode`,
    "TaxReceiptSettings.NumberingMode.Invalid": `${ERR}.numberingMode`,
    "TaxReceiptSettings.NumberingPrefix.Invalid": `${ERR}.numberingPrefix`,
    "TaxReceiptSettings.NumberingCounterSize.OutOfRange": `${ERR}.numberingCounterSize`,
    "TaxReceiptSettings.DocumentTemplate.Invalid": `${ERR}.documentTemplate`,
    // The lock flipped between the read and the save: a receipt of the year was issued meanwhile.
    "TaxReceipt.Numbering.Locked": `${ERR}.numberingLocked`,
    // Emitted by OPDON's DuplicateEntityExceptionHandler when two tabs save the singleton row at once.
    DuplicateEntity: `${ERR}.concurrentSave`,
};
function toForm(settings) {
    return {
        mode: settings.mode,
        moduleEnabled: settings.moduleEnabled,
        campaignTriggerDay: String(settings.campaignTriggerDay),
        campaignTriggerMonth: String(settings.campaignTriggerMonth),
        recipients: sortedRecipients(toRecipientOptionIds(settings.campaignNotificationUserIds, settings.campaignNotificationRoles)),
        campaignFirstFiscalYear: settings.campaignFirstFiscalYear === null ? "" : String(settings.campaignFirstFiscalYear),
        numberingMode: settings.numberingMode,
        numberingPrefix: settings.numberingPrefix,
        numberingCounterSize: String(settings.numberingCounterSize),
        numberingLocked: settings.numberingLocked,
        documentTemplateIndividualId: settings.documentTemplateIndividualId,
        documentTemplateOrganizationId: settings.documentTemplateOrganizationId,
        updatedAt: settings.updatedAt,
    };
}
// The picker appends each pick at the end, so the same selection can arrive in another order;
// the dirty check compares the state as text, so both sides keep one order.
function sortedRecipients(optionIds) {
    return [...optionIds].sort();
}
function parseInteger(value) {
    if (value.trim() === "")
        return null;
    const n = Number(value);
    return Number.isInteger(n) ? n : null;
}
export function useTaxReceiptSettingsForm() {
    const state = reactive(toForm(createDefaultTaxReceiptSettings()));
    // A ref, not a `let`: the dirty computed must re-run after a save that stores equal values.
    const snapshot = ref(JSON.stringify(state));
    // The year OPDON holds: it stays valid as the years go by, only a new choice is bounded.
    const savedFirstFiscalYear = ref(null);
    const errors = ref({});
    const isLoading = ref(false);
    const isSaving = ref(false);
    const loadError = ref(false);
    const saveErrorKey = ref(null);
    const isDirty = computed(() => JSON.stringify(state) !== snapshot.value);
    const numberPreview = computed(() => {
        const size = parseInteger(state.numberingCounterSize) ?? 6;
        return formatTaxReceiptNumberPreview(state.numberingMode, state.numberingPrefix || "RF", size, new Date().getFullYear());
    });
    /** Keycloak user ids currently selected — what the recipients picker must be able to label. */
    const selectedUserIds = computed(() => fromRecipientOptionIds(state.recipients).userIds);
    function toPayload() {
        const next = {};
        const day = parseInteger(state.campaignTriggerDay);
        const month = parseInteger(state.campaignTriggerMonth);
        if (day === null || month === null || !isValidTriggerDate(day, month)) {
            next.triggerDate = `${ERR}.triggerDate`;
        }
        const { userIds, roles } = fromRecipientOptionIds(state.recipients);
        if (userIds.length > TAX_RECEIPT_MAX_NOTIFICATION_RECIPIENTS ||
            roles.length > TAX_RECEIPT_MAX_NOTIFICATION_RECIPIENTS) {
            next.recipients = `${ERR}.recipientsTooMany`;
        }
        const firstFiscalYear = state.campaignFirstFiscalYear === "" ? null : parseInteger(state.campaignFirstFiscalYear);
        if ((state.campaignFirstFiscalYear !== "" && firstFiscalYear === null) ||
            !isValidCampaignFirstFiscalYear(firstFiscalYear, new Date().getFullYear(), savedFirstFiscalYear.value)) {
            next.campaignFirstFiscalYear = `${ERR}.campaignFirstFiscalYearRecent`;
        }
        if (!TAX_RECEIPT_NUMBERING_MODES.includes(state.numberingMode)) {
            next.numberingMode = `${ERR}.numberingMode`;
        }
        if (!isValidNumberingPrefix(state.numberingPrefix)) {
            next.numberingPrefix = `${ERR}.numberingPrefix`;
        }
        const counterSize = parseInteger(state.numberingCounterSize);
        if (counterSize === null || !TAX_RECEIPT_COUNTER_SIZES.includes(counterSize)) {
            next.numberingCounterSize = `${ERR}.numberingCounterSize`;
        }
        if (Object.keys(next).length > 0)
            return { payload: null, errors: next };
        return {
            errors: next,
            payload: {
                mode: state.mode,
                moduleEnabled: state.moduleEnabled,
                campaignTriggerDay: day,
                campaignTriggerMonth: month,
                campaignNotificationUserIds: userIds,
                campaignNotificationRoles: roles,
                campaignFirstFiscalYear: firstFiscalYear,
                numberingMode: state.numberingMode,
                numberingPrefix: state.numberingPrefix,
                numberingCounterSize: counterSize,
                documentTemplateIndividualId: state.documentTemplateIndividualId,
                documentTemplateOrganizationId: state.documentTemplateOrganizationId,
            },
        };
    }
    const isValid = computed(() => toPayload().payload !== null);
    const hasDocumentTemplates = computed(() => state.documentTemplateIndividualId !== null && state.documentTemplateOrganizationId !== null);
    function applySnapshot(settings) {
        Object.assign(state, toForm(settings));
        savedFirstFiscalYear.value = settings.campaignFirstFiscalYear;
        snapshot.value = JSON.stringify(state);
        errors.value = {};
        saveErrorKey.value = null;
    }
    async function load() {
        isLoading.value = true;
        loadError.value = false;
        try {
            applySnapshot(await getTaxReceiptSettings());
        }
        catch (err) {
            logger.error("Failed to load tax-receipt settings", err);
            loadError.value = true;
        }
        finally {
            isLoading.value = false;
        }
    }
    function setField(field, value) {
        // The lock is enforced by OPDON (409); the form mirrors it so a locked field never turns dirty.
        if (state.numberingLocked &&
            (field === "numberingMode" || field === "numberingPrefix" || field === "numberingCounterSize")) {
            return;
        }
        // A day that no longer exists in the newly picked month is clamped rather than flagged.
        if (field === "campaignTriggerMonth") {
            const month = parseInteger(value);
            const day = parseInteger(state.campaignTriggerDay);
            if (month !== null && day !== null && !isValidTriggerDate(day, month) && isValidTriggerDate(1, month)) {
                state.campaignTriggerDay = String(daysInMonth(month));
            }
        }
        state[field] = field === "recipients" ? sortedRecipients(value) : value;
        // Clear-on-correct, one field at a time: the other errors stay until the next save.
        const next = { ...errors.value };
        if (field in next)
            delete next[field];
        if (field === "campaignTriggerDay" || field === "campaignTriggerMonth")
            delete next.triggerDate;
        errors.value = next;
        saveErrorKey.value = null;
    }
    // OPDON refused the write because a receipt of the year was issued since the read. Re-read the
    // numbering only, so the lock lands (fields inert, locked badge) while the edits to the other
    // blocks survive for the next save.
    async function mirrorNumberingLock() {
        try {
            const fresh = toForm(await getTaxReceiptSettings());
            const numbering = {
                numberingLocked: fresh.numberingLocked,
                numberingMode: fresh.numberingMode,
                numberingPrefix: fresh.numberingPrefix,
                numberingCounterSize: fresh.numberingCounterSize,
            };
            Object.assign(state, numbering);
            const previous = JSON.parse(snapshot.value);
            snapshot.value = JSON.stringify({ ...previous, ...numbering });
        }
        catch (err) {
            logger.error("Failed to re-read the tax-receipt settings after a numbering lock", err);
        }
    }
    function reset() {
        Object.assign(state, JSON.parse(snapshot.value));
        errors.value = {};
        saveErrorKey.value = null;
    }
    async function save() {
        const { payload, errors: nextErrors } = toPayload();
        errors.value = nextErrors;
        if (!payload)
            return "invalid";
        isSaving.value = true;
        try {
            applySnapshot(await updateTaxReceiptSettings(payload));
            return "saved";
        }
        catch (err) {
            const key = resolveApiErrorKey(err, TAX_RECEIPT_SETTINGS_ERROR_KEYS, `${ERR}.saveFailed`);
            if (key === `${ERR}.numberingLocked`)
                await mirrorNumberingLock();
            saveErrorKey.value = key;
            return "failed";
        }
        finally {
            isSaving.value = false;
        }
    }
    return {
        state,
        errors,
        isDirty,
        isLoading,
        isSaving,
        loadError,
        saveErrorKey,
        isValid,
        hasDocumentTemplates,
        numberPreview,
        selectedUserIds,
        load,
        save,
        reset,
        setField,
    };
}
